// src/components/ExportCSVButton.jsx
import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../data/firebase";
import { useRole } from "../data/useRole";

export default function ExportCSVButton() {
  const { canEdit } = useRole();
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [isHover, setIsHover] = useState(false);
  const [isActive, setIsActive] = useState(false);

  // Countdown effect for cooldown
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((t) => t - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  if (!canEdit) return null; // Only editors see the export button

  const handleExport = async () => {
    if (loading || cooldown > 0) return;
    setLoading(true);

    try {
      const snapshot = await getDocs(collection(db, "formulations"));
      const docs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      if (docs.length === 0) {
        alert("No formulations found.");
        setLoading(false);
        return;
      }

      const rows = docs.map((d) => ({
        ID: d.id || "",
        Name: d.name || "",
        Code: d.code || "",
        Product: d.product || "",
        Base: d.base || "",
        Size_L: d.sizeL || "",
        Base_Type: d.baseType || "",
        Confidence: d.confidence || "",
        Chart: d.chart || "",
        Notes: d.notes || "",
        Creator: d.creatorEmail || "",
        Created_At:
          d.createdAt && d.createdAt.toDate
            ? d.createdAt.toDate().toLocaleString("en-NZ")
            : d.createdAt
            ? new Date(d.createdAt).toLocaleString("en-NZ")
            : "",
        Formula:
          Array.isArray(d.rows) && d.rows.length > 0
            ? d.rows
                .filter((r) => r.colorant?.trim() && String(r.value ?? "").trim())
                .map((r) => `${r.colorant}=${r.value}`)
                .join("; ")
            : "",
      }));

      const header = Object.keys(rows[0]).join(",");
      const csv = [
        header,
        ...rows.map((r) =>
          Object.values(r)
            .map((v) => `"${String(v).replace(/"/g, '""')}"`)
            .join(",")
        ),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `formulations-export-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      setCooldown(10); // start 10s cooldown
    } catch (err) {
      console.error(err);
      alert("Export failed: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const buttonLabel = loading
    ? "Exporting..."
    : cooldown > 0
    ? `Please wait… (${cooldown}s)`
    : "Export CSV";

  const style = {
    padding: "8px 14px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    background:
      loading || cooldown > 0
        ? "#777"
        : isActive
        ? "#111"
        : isHover
        ? "#333"
        : "#1a1a1a",
    color: "#fff",
    fontWeight: 500,
    cursor: loading || cooldown > 0 ? "not-allowed" : "pointer",
    opacity: loading || cooldown > 0 ? 0.7 : 1,
    transform: isActive ? "translateY(1px)" : "none",
    transition: "background-color 0.15s, transform 0.05s, opacity 0.15s",
  };

  return (
    <button
      style={style}
      onClick={handleExport}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => {
        setIsHover(false);
        setIsActive(false);
      }}
      onMouseDown={() => setIsActive(true)}
      onMouseUp={() => setIsActive(false)}
      disabled={loading || cooldown > 0}
      title="Export all formulations as CSV"
    >
      {buttonLabel}
    </button>
  );
}
